import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCartCtx, useAuthCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import api from '../utils/api';
import { productImage, onImageError } from '../utils/helpers';
import { CATEGORIES, categoryBySlug, categoryForProduct } from '../data/categories';
import { toast } from 'react-toastify';

// Lowest VIP (member) price for a product — drives the "From $X" sort + label.
const fromPriceCents = (p) =>
  Math.min(...(p.variants?.length ? p.variants.map((v) => v.priceInCents) : [Infinity]));

// Retail (non-member) unit price for a variant: comparePrice when it carries a
// markup, else the base price.
const retailOf = (v) =>
  (v.comparePriceInCents && v.comparePriceInCents > v.priceInCents) ? v.comparePriceInCents : v.priceInCents;

// Lowest retail price across a product's variants.
const fromRetailCents = (p) =>
  Math.min(...(p.variants?.length ? p.variants.map(retailOf) : [Infinity]));

const SORTS = {
  popular:    { label: 'Most popular', fn: null },
  priceAsc:   { label: 'Price: low to high',  fn: (a, b) => fromPriceCents(a) - fromPriceCents(b) },
  priceDesc:  { label: 'Price: high to low',  fn: (a, b) => fromPriceCents(b) - fromPriceCents(a) },
  nameAsc:    { label: 'Name: A to Z',        fn: (a, b) => a.name.localeCompare(b.name) },
};

// Trust bar — light sky-blue strip with icon+label chips
function TrustBar() {
  const items = [
    {
      k: 'Third-Party Tested',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 12l2 2 4-4.5M12 2l7 3v6c0 4.5-3 7.7-7 9-4-1.3-7-4.5-7-9V5l7-3z" />
        </svg>
      ),
    },
    {
      k: '≥99% Purity',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      ),
    },
    {
      k: 'USA Based',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0" />
          <path d="M3 12h18M12 3a14.5 14.5 0 0 1 0 18M12 3a14.5 14.5 0 0 0 0 18" />
        </svg>
      ),
    },
    {
      k: 'Discreet Shipping',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7zM7 18a1.8 1.8 0 1 0 0 .01M17.5 18a1.8 1.8 0 1 0 0 .01" />
        </svg>
      ),
    },
    {
      k: 'Batch Traceable',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 3h7l5 5v13H7zM14 3v5h5M9 12h6M9 16h4" />
        </svg>
      ),
    },
  ];

  return (
    <div className="bg-tea-50 border-b border-tea-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 py-2.5">
          {items.map((it) => (
            <li key={it.k} className="flex items-center gap-1.5 text-xs font-semibold text-tea-700 tracking-wide">
              <span className="text-tea-500">{it.icon}</span>
              {it.k}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function ShopPage() {
  const { fmt } = useCurrency();
  const [products, setProducts]         = useState([]);
  const [pagination, setPagination]     = useState({});
  const [loading, setLoading]           = useState(true);
  const [sort, setSort]                 = useState('popular');
  const [searchParams, setSearchParams] = useSearchParams();
  const { addItem } = useCartCtx();
  const { isMember } = useAuthCtx();

  const search   = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const page     = parseInt(searchParams.get('page') || '1');
  const activeCat = category ? categoryBySlug(category) : null;

  useEffect(() => {
    setLoading(true);
    // When a category is active we filter client-side from the (small) catalog,
    // so pull a full page rather than paginating.
    const params = category ? { search, limit: 50 } : { page, search, limit: 12 };
    api.get('/products', { params })
      .then(res => { setProducts(res.data.data); setPagination(res.data.pagination); })
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  }, [page, search, category]);

  const sorted = useMemo(() => {
    const inCategory = activeCat
      ? products.filter((p) => activeCat.members.includes(p.slug))
      : products;
    const fn = SORTS[sort]?.fn;
    return fn ? [...inCategory].sort(fn) : inCategory;
  }, [products, sort, activeCat]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = e.target.search.value;
    setSearchParams(q ? { search: q } : {});
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-tea-50 via-white to-white">
      <Navbar />
      <TrustBar />

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-8">
          <p className="text-tea-600 font-bold uppercase tracking-[0.2em] text-[11px] mb-3">
            {activeCat ? 'Compound class' : 'Research Peptide Catalog'}
          </p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight">
            {activeCat ? activeCat.name : 'Premium Research Peptides'}
          </h1>
          <p className="mt-4 text-sm text-gray-500 leading-relaxed">
            {activeCat ? (
              <>Grouped by peptide family — {activeCat.blurb}.{' '}</>
            ) : (
              <>Pharmaceutical-grade compounds, identity- and purity-verified, shipped from the USA.{' '}</>
            )}
            <span className="font-semibold text-amber-600">
              For laboratory research use only — not for human or veterinary consumption.
            </span>
          </p>
        </div>

        {/* Category filter chips */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-9">
          <button
            type="button"
            onClick={() => setSearchParams(search ? { search } : {})}
            className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
              !activeCat
                ? 'bg-tea-600 text-white border-tea-600 shadow-sm'
                : 'bg-white text-gray-600 border-gray-200 hover:border-tea-400 hover:text-tea-600'
            }`}
          >
            All products
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => setSearchParams({ category: c.slug, ...(search ? { search } : {}) })}
              className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                activeCat?.slug === c.slug
                  ? 'bg-tea-600 text-white border-tea-600 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-tea-400 hover:text-tea-600'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* VIP upsell banner — only for signed-in non-members */}
        {!isMember && (
          <Link
            to="/membership"
            className="group block mb-8 rounded-2xl bg-gradient-to-r from-tea-600 to-tea-500 text-white px-6 py-4 shadow-md hover:shadow-lg transition-all"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-3">
                <span className="text-2xl">★</span>
                <div>
                  <div className="font-bold text-sm sm:text-base">Unlock VIP pricing + free shipping</div>
                  <div className="text-tea-100 text-xs sm:text-sm">Members pay less on every product. From $5/mo.</div>
                </div>
              </div>
              <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white text-tea-700 font-bold text-sm px-5 py-2 group-hover:gap-2 transition-all">
                Become a VIP <span>→</span>
              </span>
            </div>
          </Link>
        )}

        {/* Toolbar: search + count + sort */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between mb-8">
          <form onSubmit={handleSearch} className="flex gap-2 w-full md:max-w-sm">
            <input
              name="search"
              defaultValue={search}
              placeholder="Search peptides (e.g. BPC-157)…"
              className="flex-1 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-tea-400 focus:border-transparent transition"
            />
            <button
              type="submit"
              className="rounded-full bg-tea-600 hover:bg-tea-700 text-white text-sm font-semibold px-6 py-2.5 shadow-sm transition-colors"
            >
              Search
            </button>
          </form>

          <div className="flex items-center justify-between md:justify-end gap-4">
            <span className="text-sm text-gray-400 font-medium">
              {loading
                ? 'Loading…'
                : (() => {
                    const n = activeCat ? sorted.length : (pagination.total ?? sorted.length);
                    return `${n} product${n === 1 ? '' : 's'}`;
                  })()}
            </span>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <span className="hidden sm:inline font-medium text-gray-400">Sort by</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-tea-400 focus:border-transparent transition cursor-pointer"
              >
                {Object.entries(SORTS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-white/70 animate-pulse h-96 shadow-sm" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <p className="text-gray-400 text-center py-24 text-lg">No products found.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sorted.map((product) => {
              const vipMin    = fromPriceCents(product);
              const retailMin = fromRetailCents(product);
              const hasVipDeal = retailMin > vipMin;
              const category = categoryForProduct(product.slug)?.name || product.category || 'Research Peptide';
              return (
                <div
                  key={product.id}
                  className="group flex flex-col rounded-2xl bg-white shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-200 overflow-hidden"
                >
                  {/* Image panel */}
                  <Link to={`/shop/${product.slug}`} className="block">
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={productImage(product.slug)}
                        onError={onImageError}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  </Link>

                  {/* Card body */}
                  <div className="flex flex-1 flex-col px-5 pb-5 pt-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-tea-500 mb-1">
                      {category}
                    </p>
                    <Link to={`/shop/${product.slug}`}>
                      <h3 className="font-bold text-gray-900 text-[15px] leading-snug line-clamp-2 hover:text-tea-600 transition-colors">
                        {product.name}
                      </h3>
                    </Link>

                    {isMember ? (
                      // Member: pay the VIP price, retail struck through.
                      <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-xs text-gray-400 font-medium">From</span>
                        <span className="text-lg font-extrabold text-gray-900">
                          {Number.isFinite(vipMin) ? fmt(vipMin) : '—'}
                        </span>
                        {hasVipDeal && (
                          <span className="text-xs text-gray-400 line-through">{fmt(retailMin)}</span>
                        )}
                        <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 rounded px-1.5 py-0.5">VIP</span>
                      </div>
                    ) : (
                      // Non-member: pay retail; surface the locked VIP price as an upsell.
                      <div className="mt-2">
                        <div className="flex items-baseline gap-1">
                          <span className="text-xs text-gray-400 font-medium">From</span>
                          <span className="text-lg font-extrabold text-gray-900">
                            {Number.isFinite(retailMin) ? fmt(retailMin) : '—'}
                          </span>
                        </div>
                        {hasVipDeal && (
                          <Link to="/membership" className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 hover:text-amber-700">
                            <span>★</span> VIP {fmt(vipMin)} — unlock
                          </Link>
                        )}
                      </div>
                    )}

                    {product.variants?.[0] && (
                      <button
                        onClick={() => {
                          addItem(product.variants[0], product);
                          toast.success('Added to cart!');
                        }}
                        className="mt-4 w-full rounded-full bg-tea-600 hover:bg-tea-700 text-white text-sm font-bold py-2.5 tracking-wide transition-colors shadow-sm"
                      >
                        Add to Cart
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination — only for the full, unfiltered catalog */}
        {!activeCat && pagination.pages > 1 && (
          <div className="flex justify-center gap-2 mt-14">
            {[...Array(pagination.pages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setSearchParams({ page: i + 1, ...(search ? { search } : {}) })}
                className={`min-w-[40px] px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  page === i + 1
                    ? 'bg-tea-600 text-white border-tea-600 shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-tea-400 hover:text-tea-600'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
